import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Calendar, Clock, Stethoscope, CheckCircle2, Loader2, ArrowRight } from 'lucide-react';
import { toDateIST } from '../utils/date';
import {
  isValidIndianMobile, isPartialIndianMobile,
  normalizeIndianMobile, phoneErrorMessage,
} from '../utils/phone';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PublicBookingSection — visible on the tenant landing page. Lets a
 * walk-up visitor pick a doctor + date + time slot and submit basic
 * info as a Tentative appointment request. Protected by a hidden
 * honeypot input + server-side IP rate-limit (5/min).
 */
export default function PublicBookingSection({ slug, primary }) {
  const today = new Date();
  const next14 = useMemo(() => Array.from({ length: 14 }, (_, i) => {
    const d = new Date(today); d.setDate(today.getDate() + i);
    return d;
  }), [today]);

  const fromIso = toDateIST(next14[0]);
  const toIso = toDateIST(next14[next14.length - 1]);

  const [avail, setAvail] = useState([]);
  const [picked, setPicked] = useState({ date: '', doctor_id: '', doctor_name: '', slot: '' });
  const [form, setForm] = useState({ name: '', phone: '', age: '', gender: '', symptoms: '', website: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');
  // Whether the phone field has been focused-and-blurred once —
  // suppresses the red border while the visitor is mid-keystroke.
  const [phoneTouched, setPhoneTouched] = useState(false);
  const phoneShowError = (phoneTouched && !isValidIndianMobile(form.phone))
    || (form.phone.length > 0 && !isPartialIndianMobile(form.phone));
  const phoneErrMsg = phoneShowError ? phoneErrorMessage(form.phone) : '';

  useEffect(() => {
    axios.get(`${API}/api/tenant/${slug}/availability`, { params: { from_date: fromIso, to_date: toIso } })
      .then(({ data }) => setAvail(data?.available || []))
      .catch(() => setAvail([]));
  }, [slug, fromIso, toIso]);

  // Slots derivable from picked shift
  const slotsForPick = useMemo(() => {
    if (!picked.date || !picked.doctor_id) return [];
    const shifts = avail.filter(a => a.date === picked.date && a.doctor_id === picked.doctor_id);
    const out = [];
    for (const s of shifts) {
      const [sh, sm] = s.shift_start.split(':').map(Number);
      const [eh, em] = s.shift_end.split(':').map(Number);
      const step = s.slot_minutes || 15;
      let t = sh * 60 + sm;
      const end = eh * 60 + em;
      while (t + step <= end) {
        const h = String(Math.floor(t / 60)).padStart(2, '0');
        const m = String(t % 60).padStart(2, '0');
        out.push(`${h}:${m}`);
        t += step;
      }
    }
    return out;
  }, [picked, avail]);

  const doctorsForDate = useMemo(() => {
    if (!picked.date) return [];
    const m = new Map();
    avail.filter(a => a.date === picked.date).forEach(a => {
      m.set(a.doctor_id, { id: a.doctor_id, name: a.doctor_name, spec: a.specialisation });
    });
    return Array.from(m.values());
  }, [picked.date, avail]);

  const datesWithSlots = useMemo(() => new Set(avail.map(a => a.date)), [avail]);

  const submit = async () => {
    setError('');
    if (!form.name.trim() || form.name.trim().length < 2) { setError('Please enter your full name'); return; }
    if (!isValidIndianMobile(form.phone)) {
      setPhoneTouched(true);
      setError(phoneErrorMessage(form.phone) || 'Enter a valid 10-digit Indian mobile number.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/tenant/${slug}/book`, {
        ...form,
        phone: normalizeIndianMobile(form.phone),  // stored as bare 10 digits
        ...picked,
        preferred_date: picked.date,
        preferred_slot: picked.slot,
      });
      setSuccess(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not submit — please try again.');
    }
    setSubmitting(false);
  };

  if (success) {
    return (
      <div className="bg-emerald-500/15 border border-emerald-300/30 backdrop-blur rounded-2xl p-8 text-center" data-testid="booking-success">
        <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-400 mb-3" />
        <div className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>Request received!</div>
        <p className="text-zinc-300 mt-2">{success.message}</p>
        <p className="text-xs text-zinc-400 mt-3">Reference: {success.id?.slice(-8)?.toUpperCase()}</p>
      </div>
    );
  }

  return (
    <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 text-left" data-testid="public-booking">
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-1">Book online</div>
      <div className="text-2xl font-bold text-white mb-5" style={{ fontFamily: 'Manrope' }}>Pick a doctor &amp; slot</div>

      {/* Date strip */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4" data-testid="booking-dates">
        {next14.map(d => {
          const iso = toDateIST(d);
          const has = datesWithSlots.has(iso);
          const isPicked = picked.date === iso;
          return (
            <button
              key={iso}
              disabled={!has}
              onClick={() => setPicked(p => ({ date: iso, doctor_id: '', doctor_name: '', slot: '' }))}
              className={`flex-shrink-0 px-3 py-2 rounded-lg border text-xs transition ${
                isPicked ? 'border-white bg-white text-zinc-900' :
                has ? 'border-white/15 text-zinc-200 hover:bg-white/10' :
                'border-white/5 text-zinc-600 cursor-not-allowed'
              }`}
              style={isPicked ? { background: primary, color: 'white', borderColor: primary } : {}}
              data-testid={`booking-date-${iso}`}
            >
              <div className="text-[10px] uppercase opacity-80">{d.toLocaleDateString('en-IN', { weekday: 'short', timeZone: 'Asia/Kolkata' })}</div>
              <div className="text-base font-bold">{Number(iso.slice(-2))}</div>
              <div className="text-[9px] opacity-70">{d.toLocaleDateString('en-IN', { month: 'short', timeZone: 'Asia/Kolkata' })}</div>
            </button>
          );
        })}
      </div>

      {/* Doctors */}
      {picked.date && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1.5 flex items-center gap-1"><Stethoscope className="h-3 w-3" /> Doctor</div>
          {doctorsForDate.length === 0 ? (
            <div className="text-xs text-zinc-500 italic">No doctors available on this date.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {doctorsForDate.map(d => (
                <button
                  key={d.id}
                  onClick={() => setPicked(p => ({ ...p, doctor_id: d.id, doctor_name: d.name, slot: '' }))}
                  className={`px-3 py-2 rounded-lg border text-xs ${picked.doctor_id === d.id ? 'border-white bg-white/20 text-white' : 'border-white/15 text-zinc-200 hover:bg-white/10'}`}
                  data-testid={`booking-doctor-${d.id}`}
                >
                  <div className="font-semibold">{d.name}</div>
                  {d.spec && <div className="text-[10px] opacity-70">{d.spec}</div>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Slots */}
      {picked.doctor_id && (
        <div className="mb-4">
          <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1.5 flex items-center gap-1"><Clock className="h-3 w-3" /> Time slot</div>
          <div className="flex flex-wrap gap-1.5">
            {slotsForPick.map(s => (
              <button
                key={s}
                onClick={() => setPicked(p => ({ ...p, slot: s }))}
                className={`px-2.5 py-1 rounded-md text-[11px] font-mono border ${picked.slot === s ? 'bg-white text-zinc-900 border-white' : 'text-zinc-200 border-white/15 hover:bg-white/10'}`}
                data-testid={`booking-slot-${s}`}
              >{s}</button>
            ))}
          </div>
        </div>
      )}

      {/* Form */}
      {picked.slot && (
        <div className="space-y-2.5 border-t border-white/10 pt-4" data-testid="booking-form">
          <div className="grid grid-cols-2 gap-2">
            <input
              placeholder="Full name"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-white/40"
              data-testid="booking-name"
            />
            <div className="flex flex-col">
              <input
                placeholder="10-digit mobile"
                value={form.phone}
                inputMode="tel"
                maxLength={16}
                onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                onBlur={() => setPhoneTouched(true)}
                aria-invalid={phoneShowError || undefined}
                className={`bg-white/5 border rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none ${
                  phoneShowError
                    ? 'border-red-400/70 focus:border-red-400'
                    : 'border-white/15 focus:border-white/40'
                }`}
                data-testid="booking-phone"
              />
              {phoneShowError && (
                <span className="text-[11px] text-red-300 mt-1" data-testid="booking-phone-error">
                  {phoneErrMsg}
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              placeholder="Age"
              value={form.age}
              onChange={(e) => setForm(f => ({ ...f, age: e.target.value }))}
              className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-white/40"
              data-testid="booking-age"
            />
            <select
              value={form.gender}
              onChange={(e) => setForm(f => ({ ...f, gender: e.target.value }))}
              className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-white/40"
              data-testid="booking-gender"
            >
              <option value="" className="text-black">Gender (optional)</option>
              <option value="male" className="text-black">Male</option>
              <option value="female" className="text-black">Female</option>
              <option value="other" className="text-black">Other</option>
            </select>
          </div>
          <textarea
            placeholder="Briefly, what brings you in? (optional)"
            value={form.symptoms}
            onChange={(e) => setForm(f => ({ ...f, symptoms: e.target.value }))}
            rows={2}
            className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-white/40 resize-none"
            data-testid="booking-symptoms"
          />
          {/* Honeypot — invisible to humans, bots fill it */}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={(e) => setForm(f => ({ ...f, website: e.target.value }))}
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
            aria-hidden="true"
          />
          {error && <div className="text-[11px] text-red-400 bg-red-500/10 border border-red-400/30 rounded p-2">{error}</div>}
          <button
            onClick={submit}
            disabled={submitting}
            className="w-full mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-white transition hover:scale-[1.01] disabled:opacity-60"
            style={{ background: primary }}
            data-testid="booking-submit"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            Request appointment
          </button>
          <p className="text-[10px] text-zinc-500 text-center">The clinic will call you to confirm the slot.</p>
        </div>
      )}

      {!picked.date && (
        <div className="text-center text-xs text-zinc-500 italic">
          <Calendar className="h-4 w-4 inline mb-0.5" /> Pick a date to see doctors and time slots.
        </div>
      )}
    </div>
  );
}
